import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import api from '../../services/api'
import toast from 'react-hot-toast'
import {
  Calendar,
  QrCode,
  ClipboardList,
  Camera,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Send,
  AlertCircle,
  Filter,
  ShieldCheck,
  Check,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

const SUBMISSION_STATUS_CFG = {
  PENDING:   { label: 'Pending Verification', cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/25',   Icon: Clock },
  CONFIRMED: { label: 'Confirmed',            cls: 'bg-green-500/10 text-green-400 border-green-500/25',     Icon: CheckCircle2 },
  REJECTED:  { label: 'Rejected',             cls: 'bg-red-500/10 text-red-400 border-red-500/25',           Icon: XCircle },
}

const OFFICIAL_STATUS_CFG = {
  present: { label: 'Present', cls: 'bg-green-500/10 text-green-400 border-green-500/25',   Icon: CheckCircle2 },
  absent:  { label: 'Absent',  cls: 'bg-red-500/10 text-red-400 border-red-500/25',         Icon: XCircle },
  late:    { label: 'Late',    cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/25', Icon: Clock },
  excused: { label: 'Excused', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/25',      Icon: CheckCircle2 },
}

const REASONS = [
  { value: 'FORGOT_QR', label: 'Forgot QR Code' },
  { value: 'REMOTE_CLASS', label: 'Remote Session' },
  { value: 'TECHNICAL_ISSUE', label: 'Technical Issue' },
  { value: 'OTHER', label: 'Other Reason' },
]

function fmtDateTime(s) {
  if (!s) return '—'
  return new Date(s).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function fmtDate(s) {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function Attendance() {
  const [searchParams, setSearchParams] = useSearchParams()
  const rawTab = searchParams.get('tab')
  const activeTab = ['check-in', 'records', 'submissions'].includes(rawTab) ? rawTab : 'check-in'

  // Official attendance state
  const [records, setRecords] = useState([])
  const [loadingRecords, setLoadingRecords] = useState(true)
  const [stats, setStats] = useState(null)

  // Eligible courses for check-in
  const [enrollments, setEnrollments] = useState([])
  const [loadingEnrollments, setLoadingEnrollments] = useState(true)

  // Submissions state
  const [submissions, setSubmissions] = useState([])
  const [loadingSubmissions, setLoadingSubmissions] = useState(true)

  // Filter state for Official Records tab
  const [recordsCourseFilter, setRecordsCourseFilter] = useState('ALL')
  const [recordsStatusFilter, setRecordsStatusFilter] = useState('ALL')

  // Check-In Scanner state & refs
  const [scanning, setScanning] = useState(false)
  const [manualToken, setManualToken] = useState('')
  const [selectedCourseId, setSelectedCourseId] = useState('')
  const [submittingQr, setSubmittingQr] = useState(false)
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const pollRef = useRef(null)

  // Manual Report state
  const [manualCourseId, setManualCourseId] = useState('')
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0])
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [certified, setCertified] = useState(false)
  const [submittingManual, setSubmittingManual] = useState(false)

  // ─── Camera cleanup logic ──────────────────────────────────────────────────
  const stopCamera = useCallback(() => {
    setScanning(false)
    if (pollRef.current) {
      cancelAnimationFrame(pollRef.current)
      pollRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }, [])

  // Stop camera immediately if switching away from 'check-in' tab or unmounting
  useEffect(() => {
    if (activeTab !== 'check-in') {
      stopCamera()
    }
  }, [activeTab, stopCamera])

  useEffect(() => () => stopCamera(), [stopCamera])

  // ─── Data fetchers ────────────────────────────────────────────────────────
  const fetchRecords = useCallback(async () => {
    try {
      const { data } = await api.get('/students/attendance')
      const attRecords = data.records || data || []
      const totalSessions = data.total_sessions ?? attRecords.length
      setRecords(attRecords)

      const present = attRecords.filter((a) => a.status === 'present').length
      const absent = attRecords.filter((a) => a.status === 'absent').length
      const late = attRecords.filter((a) => a.status === 'late').length
      const rate = totalSessions > 0 ? (((present + late * 0.5) / totalSessions) * 100).toFixed(1) : 0

      setStats({ total: totalSessions, present, absent, late, rate })
    } catch (err) {
      console.error('Failed to load official attendance records', err)
      toast.error('Failed to load official attendance records')
    } finally {
      setLoadingRecords(false)
    }
  }, [])

  const fetchEnrollments = useCallback(async () => {
    try {
      const { data } = await api.get('/students/dashboard')
      const eligible = (data.enrollments || []).filter((e) =>
        ['active', 'pending', 'pending_payment', 'completed'].includes(e.status)
      )
      setEnrollments(eligible)
      if (eligible.length > 0 && !selectedCourseId) {
        setSelectedCourseId(String(eligible[0].course_id))
      }
      if (eligible.length > 0 && !manualCourseId) {
        setManualCourseId(String(eligible[0].course_id))
      }
    } catch (err) {
      console.error('Failed to load courses', err)
      toast.error('Failed to load eligible courses')
    } finally {
      setLoadingEnrollments(false)
    }
  }, [selectedCourseId, manualCourseId])

  const fetchSubmissions = useCallback(async () => {
    setLoadingSubmissions(true)
    try {
      const { data } = await api.get('/attendance/self/mine')
      setSubmissions(data.submissions || [])
    } catch (err) {
      console.error('Failed to load submissions', err)
      toast.error('Failed to load your attendance submissions')
    } finally {
      setLoadingSubmissions(false)
    }
  }, [])

  useEffect(() => {
    fetchRecords()
    fetchEnrollments()
    fetchSubmissions()
  }, [fetchRecords, fetchEnrollments, fetchSubmissions])

  // ─── Tab navigation handler ───────────────────────────────────────────────
  const handleTabChange = (newTab) => {
    stopCamera()
    setSearchParams({ tab: newTab })
  }

  // Count pending submissions to show badge on tab
  const pendingCount = useMemo(() => {
    return submissions.filter((s) => s.status === 'PENDING').length
  }, [submissions])

  // ─── Check-In Scanner handlers ────────────────────────────────────────────
  const startCamera = async () => {
    if (!selectedCourseId) {
      toast.error('Select a course first')
      return
    }
    if (!('BarcodeDetector' in window)) {
      toast('Your browser does not support camera QR scanning. Enter the token manually below.', {
        icon: '📷',
      })
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
      setScanning(true)

      const detector = new window.BarcodeDetector({ formats: ['qr_code'] })
      const tick = async () => {
        if (!streamRef.current) return
        if (videoRef.current && videoRef.current.readyState >= 2) {
          try {
            const codes = await detector.detect(videoRef.current)
            if (codes && codes.length > 0) {
              const raw = codes[0].rawValue
              stopCamera()
              await handleScannedPayload(raw)
              return
            }
          } catch (_) {
            // Transient frame detection errors are expected; keep ticking
          }
        }
        pollRef.current = requestAnimationFrame(tick)
      }
      pollRef.current = requestAnimationFrame(tick)
    } catch (err) {
      console.error('Camera access failed', err)
      toast.error('Could not access camera. Enter the token manually below.')
    }
  }

  const handleScannedPayload = async (raw) => {
    let payload
    try {
      payload = JSON.parse(raw)
    } catch (_) {
      toast.error('Invalid QR code format')
      return
    }
    if (!payload.course_id || !payload.token) {
      toast.error('QR code is missing required session data')
      return
    }
    setSelectedCourseId(String(payload.course_id))
    await submitQr(payload.course_id, payload.token)
  }

  const submitQr = async (courseId, token) => {
    setSubmittingQr(true)
    try {
      const { data } = await api.post('/attendance/self/submit-qr', {
        course_id: Number(courseId),
        qr_token: token,
      })
      toast.success(data.message || 'Attendance submitted — awaiting tutor verification')
      fetchSubmissions()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit attendance')
    } finally {
      setSubmittingQr(false)
    }
  }

  const handleManualTokenSubmit = async (e) => {
    e.preventDefault()
    if (!selectedCourseId) return toast.error('Select a course')
    if (!manualToken.trim()) return toast.error('Enter the QR token')
    await submitQr(selectedCourseId, manualToken.trim())
    setManualToken('')
  }

  // ─── Manual Report handler ────────────────────────────────────────────────
  const handleManualReportSubmit = async (e) => {
    e.preventDefault()
    if (!manualCourseId) return toast.error('Select a course')
    if (!reason) return toast.error('Select a reason for manual entry')
    if (!certified) return toast.error('You must certify your presence')

    setSubmittingManual(true)
    try {
      const { data } = await api.post('/attendance/self/submit-manual', {
        course_id: Number(manualCourseId),
        session_date: sessionDate,
        reason,
        certified: true,
        note: note || null,
      })
      toast.success(data.message || 'Attendance submitted — awaiting tutor verification')
      setNote('')
      setCertified(false)
      setReason('')
      fetchSubmissions()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit attendance')
    } finally {
      setSubmittingManual(false)
    }
  }

  // ─── Filtered Official Records ────────────────────────────────────────────
  const filteredRecords = useMemo(() => {
    return records.filter((r) => {
      if (recordsCourseFilter !== 'ALL' && String(r.course_id) !== String(recordsCourseFilter)) {
        return false
      }
      if (recordsStatusFilter !== 'ALL' && r.status !== recordsStatusFilter) {
        return false
      }
      return true
    })
  }, [records, recordsCourseFilter, recordsStatusFilter])

  return (
    <div className="space-y-8">
      {/* ── Page Header ──────────────────────────────────────────────────────── */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-1">Student Portal</span>
          <h1 className="font-display text-2xl text-white font-light">Attendance &amp; Check-In</h1>
          <p className="font-sans text-sm text-cinema-muted mt-2 max-w-3xl">
            Your unified hub to check in for class sessions, track pending self-attendance submissions, and review your official academic attendance records.
          </p>
        </div>
      </div>

      {/* ── Attendance Summary (Cardless Editorial Metric Strip) ──────────────── */}
      {stats && (
        <div className="border-y border-white/5 py-6">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-8 gap-y-6">
            <div className="cinema-stat">
              <span className="cinema-stat-label">Total Sessions</span>
              <span className="cinema-stat-value">{stats.total}</span>
              <span className="font-sans text-[11px] text-cinema-muted mt-0.5">Published lessons</span>
            </div>
            <div className="cinema-stat">
              <span className="cinema-stat-label">Present</span>
              <span className="cinema-stat-value text-green-400">{stats.present}</span>
              <span className="font-sans text-[11px] text-cinema-muted mt-0.5">Confirmed check-ins</span>
            </div>
            <div className="cinema-stat">
              <span className="cinema-stat-label">Absent</span>
              <span className="cinema-stat-value text-red-400">{stats.absent}</span>
              <span className="font-sans text-[11px] text-cinema-muted mt-0.5">Unexcused</span>
            </div>
            <div className="cinema-stat">
              <span className="cinema-stat-label">Late</span>
              <span className="cinema-stat-value text-yellow-400">{stats.late}</span>
              <span className="font-sans text-[11px] text-cinema-muted mt-0.5">Half-credit</span>
            </div>
            <div className="cinema-stat">
              <span className="cinema-stat-label">Attendance Rate</span>
              <span className="cinema-stat-value text-cinema-glow">{stats.rate}%</span>
              <span className="font-sans text-[11px] text-cinema-muted mt-0.5">Official standing</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab Navigation ───────────────────────────────────────────────────── */}
      <div className="flex gap-1 border-b border-white/5 overflow-x-auto">
        <button
          type="button"
          onClick={() => handleTabChange('check-in')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-sans font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
            activeTab === 'check-in'
              ? 'text-cinema-glow border-cinema-glow'
              : 'text-cinema-muted border-transparent hover:text-white'
          }`}
        >
          <QrCode size={15} /> Check In
        </button>

        <button
          type="button"
          onClick={() => handleTabChange('records')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-sans font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
            activeTab === 'records'
              ? 'text-cinema-glow border-cinema-glow'
              : 'text-cinema-muted border-transparent hover:text-white'
          }`}
        >
          <Calendar size={15} /> Official Records
          {records.length > 0 && (
            <span className="ml-1 text-xs text-cinema-muted/60 font-mono">({records.length})</span>
          )}
        </button>

        <button
          type="button"
          onClick={() => handleTabChange('submissions')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-sans font-medium transition-all border-b-2 -mb-px whitespace-nowrap ${
            activeTab === 'submissions'
              ? 'text-cinema-glow border-cinema-glow'
              : 'text-cinema-muted border-transparent hover:text-white'
          }`}
        >
          <Clock size={15} /> My Submissions
          {pendingCount > 0 ? (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] uppercase font-mono tracking-wider bg-yellow-500/15 text-yellow-400 border border-yellow-500/30">
              {pendingCount} Pending
            </span>
          ) : submissions.length > 0 ? (
            <span className="ml-1 text-xs text-cinema-muted/60 font-mono">({submissions.length})</span>
          ) : null}
        </button>
      </div>

      {/* ── TAB 1: CHECK IN ──────────────────────────────────────────────────── */}
      {activeTab === 'check-in' && (
        <div className="space-y-10">
          <div className="cinema-glass border border-white/8 p-4 sm:p-5 flex items-start gap-3">
            <ShieldCheck size={20} className="text-cinema-gold shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm font-sans text-cinema-soft leading-relaxed">
              <span className="text-white font-medium">Session Verification Workflow:</span> Scan the active in-room QR code presented by your instructor, or submit a manual report below. All check-in submissions undergo instructor verification before appearing in your official academic records.
            </div>
          </div>

          {loadingEnrollments ? (
            <LoadingSpinner label="Loading your courses" />
          ) : enrollments.length === 0 ? (
            <div className="cinema-empty">
              <Calendar size={36} className="mx-auto mb-4 text-cinema-muted/20" />
              <p className="font-sans text-sm text-cinema-muted">
                You are not currently enrolled in any active courses eligible for attendance check-in.
              </p>
            </div>
          ) : (
            <>
              {/* QR Check-In (Scanner + Manual Token) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Camera Scanner */}
                <div className="cinema-glass border border-white/8 p-6">
                  <h2 className="font-display text-lg text-white font-medium mb-2 flex items-center gap-2">
                    <Camera size={18} className="text-cinema-glow" /> Camera QR Scanner
                  </h2>
                  <p className="font-sans text-xs text-cinema-muted mb-4">
                    Point your device camera at the QR code displayed on the classroom screen.
                  </p>

                  <label className="cinema-label block mb-2">Course</label>
                  <select
                    value={selectedCourseId}
                    onChange={(e) => setSelectedCourseId(e.target.value)}
                    className="w-full cinema-input mb-4"
                  >
                    <option value="">Select course to check in…</option>
                    {enrollments.map((e) => (
                      <option key={e.course_id} value={e.course_id}>
                        {e.title} ({e.course_code})
                      </option>
                    ))}
                  </select>

                  <div className="relative aspect-square w-full max-w-sm mx-auto bg-black/40 overflow-hidden mb-4 flex items-center justify-center border border-white/5">
                    <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
                    {!scanning && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-cinema-muted p-4 text-center">
                        <QrCode size={48} className="mb-3 opacity-30 text-cinema-muted" />
                        <p className="font-sans text-xs text-cinema-soft font-medium mb-1">Camera Idle</p>
                        <p className="font-sans text-[11px] text-cinema-muted">Click Start Scanning when ready to present your QR code</p>
                      </div>
                    )}
                    {scanning && (
                      <div className="absolute inset-8 border-2 border-cinema-glow/70 pointer-events-none animate-pulse" />
                    )}
                  </div>

                  <div className="flex gap-3">
                    {!scanning ? (
                      <button
                        type="button"
                        onClick={startCamera}
                        disabled={submittingQr}
                        className="cinema-btn-primary flex-1 text-sm flex items-center justify-center gap-2"
                      >
                        <Camera size={16} /> Start Scanning
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={stopCamera}
                        className="cinema-glass border border-red-500/30 text-red-400 hover:bg-red-500/10 flex-1 text-sm flex items-center justify-center gap-2 transition-all"
                      >
                        <XCircle size={16} /> Stop Camera
                      </button>
                    )}
                  </div>
                </div>

                {/* Manual Token Entry */}
                <div className="cinema-glass border border-white/8 p-6 flex flex-col justify-between">
                  <div>
                    <h2 className="font-display text-lg text-white font-medium mb-2 flex items-center gap-2">
                      <QrCode size={18} className="text-cinema-gold" /> Enter QR Token Manually
                    </h2>
                    <p className="font-sans text-xs text-cinema-muted mb-4">
                      If your camera is unavailable or not supported, enter the text token displayed underneath the classroom QR code.
                    </p>

                    <form onSubmit={handleManualTokenSubmit} className="space-y-4">
                      <div>
                        <label className="cinema-label block mb-2">Course</label>
                        <select
                          value={selectedCourseId}
                          onChange={(e) => setSelectedCourseId(e.target.value)}
                          className="w-full cinema-input"
                        >
                          <option value="">Select course…</option>
                          {enrollments.map((e) => (
                            <option key={e.course_id} value={e.course_id}>
                              {e.title} ({e.course_code})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="cinema-label block mb-2">Session Token</label>
                        <input
                          type="text"
                          value={manualToken}
                          onChange={(e) => setManualToken(e.target.value)}
                          placeholder="Paste or enter the token from the screen"
                          className="w-full cinema-input font-mono text-xs"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={submittingQr}
                        className="cinema-btn-primary w-full text-sm flex items-center justify-center gap-2 mt-2"
                      >
                        <Send size={16} /> Submit Token Check-In
                      </button>
                    </form>
                  </div>

                  <div className="mt-6 pt-6 border-t border-white/5 text-[11px] text-cinema-muted font-sans flex items-center gap-2">
                    <ShieldCheck size={14} className="text-cinema-gold/70 shrink-0" />
                    <span>Tokens expire 15 minutes after session initiation.</span>
                  </div>
                </div>
              </div>

              {/* Manual Report Fallback Section */}
              <div className="pt-8 border-t border-white/5">
                <div className="max-w-3xl">
                  <div className="mb-4">
                    <h2 className="font-display text-lg text-white font-medium flex items-center gap-2">
                      <ClipboardList size={18} className="text-cinema-gold" /> Manual Attendance Report (Fallback)
                    </h2>
                    <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-1">
                      Unable to scan or use a QR token? Submit a manual attendance report with a stated reason and personal certification.
                    </p>
                  </div>

                  <form onSubmit={handleManualReportSubmit} className="cinema-glass border border-white/8 p-6 space-y-5">
                    <div>
                      <label className="cinema-label block mb-2">Course</label>
                      <select
                        value={manualCourseId}
                        onChange={(e) => setManualCourseId(e.target.value)}
                        className="w-full cinema-input"
                      >
                        <option value="">Select course…</option>
                        {enrollments.map((e) => (
                          <option key={e.course_id} value={e.course_id}>
                            {e.title} ({e.course_code})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="cinema-label block mb-2">Session Date</label>
                        <input
                          type="date"
                          value={sessionDate}
                          onChange={(e) => setSessionDate(e.target.value)}
                          className="w-full cinema-input"
                          max={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div>
                        <label className="cinema-label block mb-2">Reason for Manual Report</label>
                        <select
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          className="w-full cinema-input"
                        >
                          <option value="">Select reason…</option>
                          {REASONS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="cinema-label block mb-2">Additional Note (Optional)</label>
                      <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={3}
                        maxLength={500}
                        placeholder="Provide any context for your instructor regarding your attendance…"
                        className="w-full cinema-input resize-none text-xs"
                      />
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer pt-2">
                      <input
                        type="checkbox"
                        checked={certified}
                        onChange={(e) => setCertified(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-cinema-glow"
                      />
                      <span className="font-sans text-xs sm:text-sm text-cinema-soft">
                        I certify that I was present, active, and engaged throughout this academic session.
                      </span>
                    </label>

                    <button
                      type="submit"
                      disabled={submittingManual}
                      className="cinema-btn-primary w-full text-sm flex items-center justify-center gap-2"
                    >
                      <Send size={16} /> Submit Manual Report
                    </button>
                  </form>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── TAB 2: OFFICIAL RECORDS ──────────────────────────────────────────── */}
      {activeTab === 'records' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-lg text-white font-medium">Official Academic Attendance</h2>
              <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-0.5">
                Official records recorded during classroom roll call or promoted upon tutor confirmation.
              </p>
            </div>

            {/* Client-side Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="cinema-label text-[10px]">Course:</span>
                <select
                  value={recordsCourseFilter}
                  onChange={(e) => setRecordsCourseFilter(e.target.value)}
                  className="cinema-input text-xs py-1.5 px-2.5 h-8 min-h-0"
                >
                  <option value="ALL">All Enrolled Courses</option>
                  {enrollments.map((e) => (
                    <option key={e.course_id} value={e.course_id}>
                      {e.course_code}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="cinema-label text-[10px]">Status:</span>
                <select
                  value={recordsStatusFilter}
                  onChange={(e) => setRecordsStatusFilter(e.target.value)}
                  className="cinema-input text-xs py-1.5 px-2.5 h-8 min-h-0"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                  <option value="excused">Excused</option>
                </select>
              </div>
            </div>
          </div>

          {loadingRecords ? (
            <LoadingSpinner label="Loading official attendance records" />
          ) : records.length === 0 ? (
            <div className="cinema-empty">
              <Calendar size={36} className="mx-auto mb-4 text-cinema-muted/20" />
              <p className="font-sans text-sm text-cinema-muted">No official attendance records found yet</p>
              <p className="font-sans text-xs text-cinema-muted/60 mt-1">
                Attendance records will appear here once roll call is taken or your check-in submissions are verified.
              </p>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="cinema-empty">
              <Filter size={32} className="mx-auto mb-3 text-cinema-muted/20" />
              <p className="font-sans text-sm text-cinema-muted">No attendance records match your filter</p>
              <button
                type="button"
                onClick={() => {
                  setRecordsCourseFilter('ALL')
                  setRecordsStatusFilter('ALL')
                }}
                className="cinema-btn-text text-xs mt-2"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto border-t border-white/5">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/6">
                    <th className="text-left py-3 px-2 cinema-label font-normal">Date</th>
                    <th className="text-left py-3 px-2 cinema-label font-normal">Course</th>
                    <th className="text-left py-3 px-2 cinema-label font-normal">Official Status</th>
                    <th className="text-left py-3 px-2 cinema-label font-normal">Check-in Time</th>
                    <th className="text-left py-3 px-2 cinema-label font-normal">Instructor Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => {
                    const cfg = OFFICIAL_STATUS_CFG[record.status] || OFFICIAL_STATUS_CFG.absent
                    const Icon = cfg.Icon
                    return (
                      <tr key={record.id} className="border-b border-white/4 hover:bg-white/[0.02] transition-colors">
                        <td className="py-4 px-2 font-sans text-xs sm:text-sm text-cinema-soft whitespace-nowrap">
                          {fmtDate(record.date)}
                        </td>
                        <td className="py-4 px-2 font-sans text-xs sm:text-sm text-white font-medium">
                          {record.course_title}
                          {record.course_code && (
                            <span className="block text-cinema-muted text-xs font-normal">{record.course_code}</span>
                          )}
                        </td>
                        <td className="py-4 px-2">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm ${cfg.cls}`}>
                            <Icon size={12} /> {cfg.label}
                          </span>
                        </td>
                        <td className="py-4 px-2 font-sans text-xs text-cinema-muted whitespace-nowrap">
                          {record.check_in_time ? fmtDateTime(record.check_in_time) : '—'}
                        </td>
                        <td className="py-4 px-2 font-sans text-xs sm:text-sm text-cinema-muted">
                          {record.notes || '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB 3: MY SUBMISSIONS ───────────────────────────────────────────── */}
      {activeTab === 'submissions' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-lg text-white font-medium">Self-Attendance Submissions</h2>
              <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-0.5">
                Review the real-time status of your QR scans and manual attendance reports.
              </p>
            </div>

            <button
              type="button"
              onClick={fetchSubmissions}
              disabled={loadingSubmissions}
              className="cinema-btn-text text-xs flex items-center gap-1.5 self-start sm:self-auto"
            >
              <RefreshCw size={13} className={loadingSubmissions ? 'animate-spin' : ''} /> Refresh Queue
            </button>
          </div>

          {/* Lifecycle guide strip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-y border-white/5 py-4 text-xs font-sans">
            <div className="flex items-start gap-2.5 text-cinema-muted">
              <Clock size={16} className="text-yellow-400 shrink-0 mt-0.5" />
              <div>
                <span className="text-yellow-400 font-medium block">Pending Verification</span>
                Awaiting instructor review. Not counted in official attendance percentage until confirmed.
              </div>
            </div>
            <div className="flex items-start gap-2.5 text-cinema-muted">
              <CheckCircle2 size={16} className="text-green-400 shrink-0 mt-0.5" />
              <div>
                <span className="text-green-400 font-medium block">Confirmed</span>
                Accepted by instructor and promoted directly to Official Academic Records.
              </div>
            </div>
            <div className="flex items-start gap-2.5 text-cinema-muted">
              <XCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
              <div>
                <span className="text-red-400 font-medium block">Rejected</span>
                Declined by instructor. Review the reason note attached below.
              </div>
            </div>
          </div>

          {loadingSubmissions ? (
            <LoadingSpinner label="Loading your submissions" />
          ) : submissions.length === 0 ? (
            <div className="cinema-empty">
              <Clock size={36} className="mx-auto mb-4 text-cinema-muted/20" />
              <p className="font-sans text-sm text-cinema-muted">No attendance submissions recorded yet</p>
              <button
                type="button"
                onClick={() => handleTabChange('check-in')}
                className="cinema-btn-primary text-xs mt-3"
              >
                Go to Check In
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/6">
                    <th className="text-left py-3 px-2 cinema-label font-normal">Session Date</th>
                    <th className="text-left py-3 px-2 cinema-label font-normal">Course</th>
                    <th className="text-left py-3 px-2 cinema-label font-normal">Method</th>
                    <th className="text-left py-3 px-2 cinema-label font-normal">Reason</th>
                    <th className="text-left py-3 px-2 cinema-label font-normal">Verification Status</th>
                    <th className="text-left py-3 px-2 cinema-label font-normal">Submitted At</th>
                    <th className="text-left py-3 px-2 cinema-label font-normal">Instructor Note / Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s) => {
                    const cfg = SUBMISSION_STATUS_CFG[s.status] || SUBMISSION_STATUS_CFG.PENDING
                    const Icon = cfg.Icon
                    return (
                      <tr key={s.id} className="border-b border-white/4 hover:bg-white/[0.02] transition-colors">
                        <td className="py-3.5 px-2 font-sans text-xs text-cinema-soft whitespace-nowrap">
                          {fmtDate(s.session_date)}
                        </td>
                        <td className="py-3.5 px-2 font-sans text-xs sm:text-sm text-white font-medium">
                          {s.course_title}
                          <span className="block text-cinema-muted text-xs font-normal">{s.course_code}</span>
                        </td>
                        <td className="py-3.5 px-2 font-sans text-xs text-cinema-muted whitespace-nowrap">
                          {s.method === 'QR_SCAN' ? (
                            <span className="inline-flex items-center gap-1 text-cinema-glow">
                              <QrCode size={12} /> QR Scan
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-cinema-soft">
                              <ClipboardList size={12} /> Manual
                            </span>
                          )}
                        </td>
                        <td className="py-3.5 px-2 font-sans text-xs text-cinema-muted">
                          {s.manual_reason ? REASONS.find((r) => r.value === s.manual_reason)?.label || s.manual_reason : '—'}
                        </td>
                        <td className="py-3.5 px-2 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-sans font-medium uppercase tracking-wide border rounded-sm ${cfg.cls}`}>
                            <Icon size={12} /> {cfg.label}
                          </span>
                        </td>
                        <td className="py-3.5 px-2 font-sans text-xs text-cinema-muted whitespace-nowrap">
                          {fmtDateTime(s.submitted_at)}
                        </td>
                        <td className="py-3.5 px-2 font-sans text-xs">
                          {s.rejection_note ? (
                            <div className="text-red-400 font-medium flex items-start gap-1">
                              <AlertCircle size={13} className="shrink-0 mt-0.5" />
                              <span>{s.rejection_note}</span>
                            </div>
                          ) : s.submission_note ? (
                            <span className="text-cinema-muted">{s.submission_note}</span>
                          ) : (
                            <span className="text-cinema-muted/50">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
